/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AIToolAccessSummary,
  AIToolAccessSummary$inboundSchema,
} from "./aitoolaccesssummary.js";

/**
 * Detection target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally). From the catalog for ids it knows, otherwise as recorded at detection time.
 */
export const Category = {
  Harness: "harness",
  Assistant: "assistant",
  LocalModel: "local_model",
} as const;
/**
 * Detection target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally). From the catalog for ids it knows, otherwise as recorded at detection time.
 */
export type Category = ClosedEnum<typeof Category>;

export const Signals = {
  Installed: "installed",
  Running: "running",
} as const;
export type Signals = ClosedEnum<typeof Signals>;

/**
 * One AI detection target aggregated across an organization's device-agent scan reports.
 */
export type AIDetection = {
  /**
   * The enforcement verdict for one AI tool, computed server-side so a client renders wording without re-deriving it. state shares its vocabulary with the shadow MCP inventory's access summary, so one column describes both halves of the Shadow AI section.
   */
  access?: AIToolAccessSummary | undefined;
  /**
   * Detection target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally). From the catalog for ids it knows, otherwise as recorded at detection time.
   */
  category: Category;
  /**
   * Distinct devices, by hardware serial, this tool was detected on. Devices that report no serial are not counted.
   */
  deviceCount: number;
  /**
   * Human-readable name from the server's detection target catalog. Ids the catalog does not know — agent binaries can ship newer target lists — fall back to the raw id.
   */
  displayName: string;
  /**
   * When this tool was first detected anywhere in the organization.
   */
  firstSeen: Date;
  /**
   * When this tool was most recently detected.
   */
  lastSeen: Date;
  /**
   * Detection signals observed for this target across all reports: installed and/or running.
   */
  signals: Array<Signals>;
  /**
   * Id of the detected AI tool as reported by agents (e.g. claude-code, ollama).
   */
  targetId: string;
  /**
   * Distinct enrolled users this tool was detected for.
   */
  userCount: number;
  /**
   * Unique non-empty detected versions for this target.
   */
  versions: Array<string>;
};

/** @internal */
export const Category$inboundSchema: z.ZodMiniEnum<typeof Category> = z.enum(
  Category,
);

/** @internal */
export const Signals$inboundSchema: z.ZodMiniEnum<typeof Signals> = z.enum(
  Signals,
);

/** @internal */
export const AIDetection$inboundSchema: z.ZodMiniType<AIDetection, unknown> = z
  .pipe(
    z.object({
      access: z.optional(AIToolAccessSummary$inboundSchema),
      category: Category$inboundSchema,
      device_count: z.int(),
      display_name: z.string(),
      first_seen: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      last_seen: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      signals: z.array(Signals$inboundSchema),
      target_id: z.string(),
      user_count: z.int(),
      versions: z.array(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "device_count": "deviceCount",
        "display_name": "displayName",
        "first_seen": "firstSeen",
        "last_seen": "lastSeen",
        "target_id": "targetId",
        "user_count": "userCount",
      });
    }),
  );

export function aiDetectionFromJSON(
  jsonString: string,
): SafeParseResult<AIDetection, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AIDetection$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AIDetection' from JSON`,
  );
}
